import {
  Box, Collapse,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material'
import { Delete, Edit, ExpandCircleDown } from '@mui/icons-material'
import React, { useState } from 'react'
import { LockedActivityActionTooltip } from '../LockedActivityActionTooltip'
import UpdateIcon from '@mui/icons-material/Update'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import { type ActivityLock } from '../../../../models/ActivityLock'

export interface BasicTableColumn {
  id: string
  label: string
  minWidth?: number
  align?: 'right' | 'left' | 'center'
}

export interface BasicTableRow {
  id: number
  key?: string
  [key: string]: JSX.Element | string | number | any
  isUpdated?: boolean
  isTracked?: boolean
  needsReview?: boolean
  expandedContent?: JSX.Element | string | number | any
  activityLock?: ActivityLock
}

interface Props {
  columns: BasicTableColumn[]
  rows: BasicTableRow[]
  isLoading: boolean
  dataTestId?: string
  hideExpand?: boolean
  readonly?: boolean
  trackingReadonly?: boolean
  indexRowIds?: boolean
  hideDelete?: boolean
  onEdit: (row: any) => void
  onDelete: (row: any) => void
  implementationPhase?: boolean
  onUpdate?: (row: any) => void
  onTrack?: (row: any) => void
  budgetOverviewView?: boolean
}

export const BasicTable = (props: Props): JSX.Element => {
  const [expandedRow, setExpandedRow] = useState<number | null>(null)
  const handleExpandClick = (rowId: number): void => {
    setExpandedRow(rowId === expandedRow ? null : rowId)
  }

  const budgetOverviewView = props.budgetOverviewView
  const allowUpdates = props.readonly !== true && budgetOverviewView == null
  const allowTracking = props.trackingReadonly !== true && budgetOverviewView == null
  const showExpandButton = props.hideExpand !== true
  const showActionButtons = allowUpdates || allowTracking || showExpandButton

  function getKey (row: BasicTableRow, index: number): number {
    return props.indexRowIds === true ? index : row.id
  }

  const rowDataTestId = (index: number): string => {
    return props.dataTestId != null ? `${props.indexRowIds === true ? index : ''}${props.dataTestId}Row` : 'basicTableRow'
  }

  interface BasicTableButtonsProps {
    row: BasicTableRow
    index: number
  }
  const BasicTableButtons = ({ row, index }: BasicTableButtonsProps): JSX.Element | null => {
    if (!showActionButtons) return null

    const EditOrUpdateButton = (): JSX.Element => {
      return props.implementationPhase === true
        ? (
          <IconButton
            data-testid={`${props.indexRowIds === true ? index : ''}updateRowButton`}
            aria-label='Update Button'
            sx={{ color: 'var(--grey-700)' }}
            onClick={() => { props.onUpdate?.(row) }}
          >
            <UpdateIcon />
          </IconButton>
          )
        : (
          <IconButton
            data-testid={`${props.indexRowIds === true ? index : ''}editRowButton`}
            aria-label='Edit Button'
            sx={{ color: 'var(--grey-700)' }}
            onClick={() => { props.onEdit(row) }}
          >
            <Edit />
          </IconButton>
          )
    }

    const TrackingButton = (): JSX.Element | null => {
      if (props.implementationPhase !== true) return null
      return (
        <IconButton
          data-testid={`${props.indexRowIds === true ? index : ''}trackRowButton`}
          aria-label='Track Button'
          disabled={row.isUpdated !== true || row.disableTracking}
          sx={{ color: 'var(--grey-700)' }}
          onClick={() => { props.onTrack?.(row) }}
        >
          <TrackChangesIcon />
        </IconButton>
      )
    }

    const DeleteButton = (): JSX.Element | null => {
      if (props.hideDelete === true) return null
      return (
        <IconButton
          data-testid={`${props.indexRowIds === true ? index : ''}deleteRowButton`}
          aria-label='Delete Button'
          sx={{ color: 'var(--grey-700)' }}
          onClick={() => { props.onDelete(row) }}
        >
          <Delete />

        </IconButton>
      )
    }

    const ExpandButton = (): JSX.Element | null => {
      if (props.hideExpand === true) return null
      return (
        <IconButton
          data-testid={`${props.indexRowIds === true ? index : ''}expandRowButton`}
          aria-label='Expand Row Button'
          sx={{
            color: 'var(--grey-700)',
            transform: expandedRow === getKey(row, index) ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease-in-out'
          }}
          onClick={() => { handleExpandClick(getKey(row, index)) }}
        >
          <ExpandCircleDown />
        </IconButton>
      )
    }

    return (
      <TableCell>
        <LockedActivityActionTooltip lock={row.activityLock}>
          <Box sx={{ display: 'flex' }}>
            {(allowUpdates || allowTracking) && (
              <>
                {allowUpdates && <EditOrUpdateButton />}

                {allowTracking && <TrackingButton />}

                {allowUpdates && <DeleteButton />}
              </>
            )}

            <ExpandButton />
          </Box>
        </LockedActivityActionTooltip>
      </TableCell>
    )
  }

  return (
    <TableContainer data-testid={props.dataTestId ?? 'basicTable'} sx={{ overflow: 'auto' }}>
      <Table style={{ breakInside: 'avoid' }}>
        <TableHead>
          <TableRow key={'header'}>
            {props.columns.map(column => (
              <TableCell key={column.id} align={column.align} style={{ minWidth: column.minWidth }}>
                <Typography variant='body2' fontWeight='600'>{column.label.toUpperCase()}</Typography>
              </TableCell>
            ))}

            {showActionButtons &&
              <TableCell sx={{ fontWeight: 600 }}>ACTION</TableCell>
            }
          </TableRow>
        </TableHead>

        <TableBody>
          {props.rows.map((row, index) => {
            const isExpanded = expandedRow === getKey(row, index)

            let leftBorder = 'none'
            if (row.needsReview === true && budgetOverviewView === true) {
              leftBorder = '.5em solid var(--blue-500)'
            } else if (row.isTracked === true && budgetOverviewView !== true) {
              leftBorder = '.5em solid var(--grey-500)'
            } else if (row.isUpdated === true && budgetOverviewView !== true) {
              leftBorder = '.5em solid var(--grey-300)'
            }

            return <React.Fragment key={row.key ?? getKey(row, index)}>
              <TableRow
                sx={{ verticalAlign: 'top', borderLeft: leftBorder }}
                data-testid={rowDataTestId(index)}
                hover={expandedRow === getKey(row, index)}
                tabIndex={-1}
                key={row.id}
                style={{ backgroundColor: isExpanded ? 'rgba(217, 217, 217, 1)' : 'inherit' }}
              >
                {props.columns.map(column => {
                  const isArray = Array.isArray(row[column.id])
                  return <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      whiteSpace: isExpanded ? 'normal' : 'nowrap',
                      overflow: 'hidden',
                      textOverflow: isExpanded ? 'clip' : 'ellipsis',
                      maxWidth: column.minWidth,
                      alignContent: 'center'
                    }}
                    data-testid={props.dataTestId != null ? `${props.indexRowIds === true ? index : ''}${props.dataTestId}${column.label}` : 'basicTableRow'}
                  >
                    {isArray
                      ? row[column.id].map((val: string, index: number) => (<Typography variant='body1' sx={{ mt: '0px' }} key={index}>{val}</Typography>))
                      : row[column.id]
                    }
                  </TableCell>
                })}

                <BasicTableButtons row={row} index={index} />
              </TableRow>
              {props.implementationPhase === true && row?.expandedContent != null && (
                <TableRow key={`${getKey(row, index)}expandedRow`} sx={{ backgroundColor: 'var(--grey-100`)' }}>
                  <TableCell style={{ padding: 0 }} colSpan={props.columns.length + 1}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      {row.expandedContent}
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
